import { useState } from 'react';
import { Clock, ShieldAlert, PlayCircle, Copy, Check, Mail, Link, MoreVertical } from 'lucide-react';
import CountdownTimer from './CountdownTimer';
import axiosClient from '../../api/axiosClient';

export default function TrialsTable({ trials = [], refreshTrials }) {
  const [provisioningId, setProvisioningId] = useState(null);
  const [sendingMailId, setSendingMailId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
  };

  const handleAction = async (id, action) => {
    if (action === 'terminate') {
      const confirmed = window.confirm("Are you sure? This will permanently drop the schema and reclaim storage immediately.");
      if (!confirmed) return;
    }

    if (action === 'provision') {
      setProvisioningId(id);
    }

    try {
      const res = await axiosClient.post('/admin/trials/action', { id, action });
      refreshTrials();
    } catch (err) {
      showToast("Failed to perform action: " + (err.response?.data?.error || err.message), 'error');
    } finally {
      setProvisioningId(null);
    }
  };

  const copyToClipboard = (id, text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleSendMail = async (id) => {
    setSendingMailId(id);
    try {
      await axiosClient.post(`/admin/trials/${id}/send-mail`);
      showToast("Mail sent successfully!");
    } catch (err) {
      showToast("Failed to send mail: " + (err.response?.data?.error || err.message), 'error');
    } finally {
      setSendingMailId(null);
    }
  };

  return (
    <div className="bg-white dark:bg-[#1e2336] border border-slate-200 dark:border-slate-700 rounded-2xl overflow-visible shadow-sm">
      <div className="overflow-visible">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50 dark:bg-[#0b1220] border-b border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 text-sm uppercase tracking-wider">
              <th className="p-4 font-medium rounded-tl-2xl">Organization</th>
              <th className="p-4 font-medium">Contact</th>
              <th className="p-4 font-medium">Trial ID</th>
              <th className="p-4 font-medium">Status</th>
              <th className="p-4 font-medium">Dates</th>
              <th className="p-4 font-medium">Time Remaining</th>
              <th className="p-4 font-medium text-right rounded-tr-2xl">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {trials.map(trial => (
              <tr key={trial.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                <td className="p-4">
                  <div className="font-medium text-slate-900 dark:text-white">{trial.school_name}</div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">{trial.client_name}</div>
                </td>
                <td className="p-4">
                  <div className="text-sm text-slate-900 dark:text-white">{trial.email}</div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">{trial.phone || '—'}</div>
                </td>
                <td className="p-4">
                  <span className={`px-2 py-1 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-mono rounded-md ${!trial.schema_name && 'opacity-50'}`}>
                    {trial.schema_name || 'Unprovisioned'}
                  </span>
                </td>
                <td className="p-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize
                    ${trial.status === 'active' ? 'bg-emerald-100 text-emerald-700 dark:text-emerald-400' : 
                      trial.status === 'expired' ? 'bg-amber-100 text-amber-700' : 
                      trial.status === 'pending' ? 'bg-primary-100 text-primary-700 dark:text-primary-400' :
                      'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'}`}>
                    {trial.status}
                  </span>
                </td>
                <td className="p-4">
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    <span className="font-semibold text-slate-700 dark:text-slate-200">Start:</span> {new Date(trial.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                  </div>
                  {trial.expires_at ? (() => {
                    const diffMs = new Date(trial.expires_at).getTime() - new Date(trial.created_at).getTime();
                    const isExtended = diffMs > (30 * 24 * 60 * 60 * 1000 + 60000); // More than 30 days
                    return (
                      <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        <span className="font-semibold text-emerald-600 dark:text-emerald-500">
                          {isExtended ? 'Extended End:' : 'End:'}
                        </span> {new Date(trial.expires_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                      </div>
                    );
                  })() : null}
                </td>
                <td className="p-4">
                  {trial.status === 'active' ? (
                    <CountdownTimer expiresAt={trial.expires_at} />
                  ) : (
                    <span className="text-slate-400 font-medium">—</span>
                  )}
                </td>
                <td className="p-4 text-right relative">
                  <button 
                    onClick={() => setOpenDropdownId(openDropdownId === trial.id ? null : trial.id)}
                    className="p-2 text-slate-400 hover:text-slate-600 dark:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700/50 dark:bg-slate-800 transition-colors"
                  >
                    <MoreVertical size={20} />
                  </button>

                  {openDropdownId === trial.id && (
                    <>
                      <div 
                        className="fixed inset-0 z-10" 
                        onClick={() => setOpenDropdownId(null)}
                      ></div>
                      <div className="absolute right-8 top-12 w-48 bg-white dark:bg-[#1e2336] rounded-xl shadow-lg border border-slate-100 dark:border-slate-800 py-2 z-20 animate-in fade-in zoom-in-95 duration-200">
                        {trial.status === 'pending' && (
                          <button 
                            onClick={() => { handleAction(trial.id, 'provision'); setOpenDropdownId(null); }}
                            disabled={provisioningId === trial.id}
                            className="w-full px-4 py-2 text-left text-sm text-primary-600 dark:text-primary-500 hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:bg-[#0b1220] font-medium flex items-center"
                          >
                            <PlayCircle size={16} className={`mr-2 ${provisioningId === trial.id && 'animate-spin'}`} /> 
                            {provisioningId === trial.id ? 'Approving...' : 'Approve Trial'}
                          </button>
                        )}
                        {trial.status === 'active' && (
                          <>
                            <button 
                              onClick={() => { handleAction(trial.id, 'extend'); setOpenDropdownId(null); }}
                              className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:bg-[#0b1220] font-medium flex items-center"
                            >
                              <Clock size={16} className="mr-2 text-slate-400" /> Extend 24h
                            </button>
                            <button 
                              onClick={() => { copyToClipboard(`link-${trial.id}`, trial.sso_url); setOpenDropdownId(null); }}
                              className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:bg-[#0b1220] font-medium flex items-center"
                            >
                              {copiedId === `link-${trial.id}` ? <Check size={16} className="text-emerald-500 mr-2" /> : <Link size={16} className="mr-2 text-slate-400" />}
                              Copy Link
                            </button>
                            <button 
                              onClick={() => { handleSendMail(trial.id); setOpenDropdownId(null); }}
                              disabled={sendingMailId === trial.id}
                              className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:bg-[#0b1220] font-medium flex items-center"
                            >
                              <Mail size={16} className={`mr-2 text-slate-400 ${sendingMailId === trial.id && 'animate-pulse'}`} /> 
                              {sendingMailId === trial.id ? 'Sending...' : 'Send Mail'}
                            </button>
                          </>
                        )}
                        {(trial.status === 'active' || trial.status === 'expired') && (
                          <button 
                            onClick={() => { handleAction(trial.id, 'terminate'); setOpenDropdownId(null); }}
                            className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-500 hover:bg-red-50 dark:bg-red-900/20 font-medium flex items-center"
                          >
                            <ShieldAlert size={16} className="mr-2" /> Terminate
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </td>
              </tr>
            ))}
            
            {trials.length === 0 && (
              <tr>
                <td colSpan="7" className="p-8 text-center text-slate-500 dark:text-slate-400">
                  No trials found. Wait for new signups!
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Custom Toast Notification */}
      {toast.show && (
        <div className={`fixed bottom-6 right-6 px-6 py-3 rounded-xl shadow-lg border flex items-center gap-3 z-50 animate-in fade-in slide-in-from-bottom-5 duration-300 ${
          toast.type === 'error' ? 'bg-red-50 dark:bg-red-900/20 text-red-900 border-red-200' : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-900 border-emerald-200'
        }`}>
          {toast.type === 'error' ? (
            <ShieldAlert size={20} className="text-red-500" />
          ) : (
            <Check size={20} className="text-emerald-500" />
          )}
          <span className="font-medium text-sm">{toast.message}</span>
        </div>
      )}
    </div>
  );
}
